import { useMutation, useQuery, useQueryClient } from 'react-query'
import { authService, usuarioService, campanhaService, participanteService, parametroSorteioService, configuracoesService, sorteioService, monitoramentoService, authUtils } from '../services/auth'
import type {
  UpdatePasswordRequest,
  UpdateUsuarioRequest,
  CampanhaFilters,
  ParticipanteFilters,
  CreateParametroSorteioRequest,
  UpdateParametroSorteioRequest,
  ConfiguracaoSistema,
  UpdateCampanhaRequest,
  LoginResponse,
} from '../types'
import toast from 'react-hot-toast'
import { getErrorMessage, isErrorHandled } from '../utils/errorHandler'

// Hook para login
export const useLogin = () => {
  const queryClient = useQueryClient()
  
  return useMutation(authService.login, {
    onSuccess: (response) => {
      console.log('Login success response:', response)
      
      // Se 2FA está habilitado (TRUE), o backend retorna LoginTwoFactorResponse
      // com email e message - Neste caso, não fazer login aqui, deixar o componente mostrar o campo de código
      if (('requiresTwoFactor' in response && response.requiresTwoFactor === true) ||
          ('email' in response && 'message' in response && !('token' in response))) {
        return // Retorna sem fazer login - o componente Login.tsx vai mostrar o campo de 6 dígitos
      }
      
      // Se 2FA está desabilitado (FALSE), o backend retorna LoginResponse direto
      // com token e usuario - Neste caso, fazer login direto sem campo 2FA
      if (response && 'token' in response && response.token && 'usuario' in response) {
        const loginResponse = response as LoginResponse
        authUtils.saveAuthData(loginResponse)
        queryClient.setQueryData('currentUser', loginResponse.usuario)
        // Forçar atualização do contexto
        window.dispatchEvent(new CustomEvent('auth-login', { 
          detail: loginResponse.usuario 
        }))
        toast.success('Login realizado com sucesso!', {
          duration: 5000
        })
      }
    },
    onError: (error: any) => {
      console.error('Login error:', error)
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        
        // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
        const is2FAError = errorMessage.toLowerCase().includes('código de verificação') || 
                          errorMessage.toLowerCase().includes('código 2fa') ||
                          errorMessage.toLowerCase().includes('verificação') ||
                          errorMessage.toLowerCase().includes('2fa') ||
                          errorMessage.toLowerCase().includes('solicite outro')
        
        // Verificar se é erro de login específico (email/senha inválidos, usuário inativo, etc)
        const isLoginError = errorMessage.toLowerCase().includes('email ou senha') ||
                            errorMessage.toLowerCase().includes('inativo') ||
                            errorMessage.toLowerCase().includes('validação') ||
                            errorMessage.toLowerCase().includes('enviar código')
        
        // Duração baseada no tipo de erro
        let errorDuration = 8000 // padrão
        if (is2FAError) {
          errorDuration = 10000 // 10 segundos para erros 2FA
        } else if (isLoginError) {
          errorDuration = 8000 // 8 segundos para erros de login
        }
        
        toast.error(errorMessage, {
          duration: errorDuration
        })
      }
    },
  })
}

// Hook para solicitar código 2FA para registro
export const useSolicitarRegistro2FA = () => {
  return useMutation(authService.solicitarRegistro2FA, {
    onSuccess: (data) => {
      toast.success(data.message || 'Código de verificação enviado para seu e-mail!', {
        duration: 10000
      })
    },
    onError: (error: any) => {
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        toast.error(errorMessage, {
          duration: 10000
        })
      }
    },
  })
}

// Hook para registro com validação 2FA
export const useRegister = () => {
  return useMutation(authService.register, {
    onSuccess: (data) => {
      if (data.aguardandoAprovacao) {
        toast.success(data.message || 'Cadastro realizado! Aguardando aprovação de um administrador.', {
          duration: 12000
        })
      } else {
        toast.success('Usuário criado com sucesso!')
      }
    },
    onError: (error: any) => {
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
        const is2FAError = errorMessage.toLowerCase().includes('código de verificação') || 
                          errorMessage.toLowerCase().includes('código 2fa') ||
                          errorMessage.toLowerCase().includes('verificação') ||
                          errorMessage.toLowerCase().includes('2fa')
        
        toast.error(errorMessage, {
          duration: is2FAError ? 10000 : 8000, // 10 segundos para erros 2FA, 8 para outros
        })
      }
    },
  })
}

// Hook para registro de admin
export const useRegisterAdmin = () => {
  return useMutation(authService.registerAdmin, {
    onSuccess: () => {
      toast.success('Administrador criado com sucesso!')
    },
    onError: (error: any) => {
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
        const is2FAError = errorMessage.toLowerCase().includes('código de verificação') || 
                          errorMessage.toLowerCase().includes('código 2fa') ||
                          errorMessage.toLowerCase().includes('verificação') ||
                          errorMessage.toLowerCase().includes('2fa')
        
        toast.error(errorMessage, {
          duration: is2FAError ? 10000 : 8000, // 10 segundos para erros 2FA, 8 para outros
        })
      }
    },
  })
}

// Hook para logout
export const useLogout = () => {
  const queryClient = useQueryClient()
  
  return useMutation(authService.logout, {
    onSuccess: () => {
      authUtils.clearAuthData()
      queryClient.clear()
      // Forçar atualização do contexto
      window.dispatchEvent(new CustomEvent('auth-logout'))
      toast.success('Logout realizado com sucesso!')
    },
    onError: () => {
      // Mesmo com erro, limpar dados locais
      authUtils.clearAuthData()
      queryClient.clear()
      // Forçar atualização do contexto mesmo com erro
      window.dispatchEvent(new CustomEvent('auth-logout'))
      toast.error('Erro ao fazer logout')
    },
  })
}

// Hook para refresh token
export const useRefreshToken = () => {
  return useMutation(
    () => authService.refreshToken(),
    {
      onSuccess: (response) => {
        if (response.success && response.data) {
          localStorage.setItem('authToken', response.data.token)
        }
      },
      onError: () => {
        authUtils.clearAuthData()
        window.location.href = '/login'
      },
    }
  )
}

// Hook para verificar código 2FA
export const useVerify2FA = () => {
  const queryClient = useQueryClient()
  
  return useMutation(authService.verify2FA, {
    onSuccess: (response) => {
      if (response && response.token && 'usuario' in response) {
        const loginResponse = response as LoginResponse
        authUtils.saveAuthData(loginResponse)
        queryClient.setQueryData('currentUser', loginResponse.usuario)
        window.dispatchEvent(new CustomEvent('auth-login', { 
          detail: loginResponse.usuario 
        }))
        toast.success('Login realizado com sucesso!', {
          duration: 5000
        })
      }
    },
    onError: (error: any) => {
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        // Erros de 2FA precisam de mais tempo para leitura
        toast.error(errorMessage, {
          duration: 10000, // 10 segundos para erros de verificação 2FA
        })
      }
    },
  })
}

// Hook para esqueci minha senha
export const useForgotPassword = () => {
  return useMutation(authService.forgotPassword, {
    onSuccess: () => {
      toast.success('E-mail de recuperação enviado! Verifique sua caixa de entrada.')
    },
    onError: (error: any) => {
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
        const is2FAError = errorMessage.toLowerCase().includes('código de verificação') || 
                          errorMessage.toLowerCase().includes('código 2fa') ||
                          errorMessage.toLowerCase().includes('verificação') ||
                          errorMessage.toLowerCase().includes('2fa')
        
        toast.error(errorMessage, {
          duration: is2FAError ? 10000 : 8000, // 10 segundos para erros 2FA, 8 para outros
        })
      }
    },
  })
}

// Hook para resetar senha
export const useResetPassword = () => {
  return useMutation(authService.resetPassword, {
    onSuccess: () => {
      toast.success('Senha resetada com sucesso!')
    },
    onError: (error: any) => {
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
        const is2FAError = errorMessage.toLowerCase().includes('código de verificação') || 
                          errorMessage.toLowerCase().includes('código 2fa') ||
                          errorMessage.toLowerCase().includes('verificação') ||
                          errorMessage.toLowerCase().includes('2fa')
        
        toast.error(errorMessage, {
          duration: is2FAError ? 10000 : 8000, // 10 segundos para erros 2FA, 8 para outros
        })
      }
    },
  })
}

// Hook para configurar 2FA
export const useConfig2FA = () => {
  const queryClient = useQueryClient()
  
  return useMutation(authService.config2FA, {
    onSuccess: (_response, _variables) => {
      // Invalidar e remover cache completamente
      queryClient.invalidateQueries('2fa-status')
      queryClient.removeQueries('2fa-status')
      
      // Atualizar cache diretamente com o novo valor
      queryClient.setQueryData('2fa-status', { enabled: _variables.enabled })
      
      // Mostrar mensagem apropriada baseada no status
      if (_variables.enabled) {
        toast.success('✅ 2FA habilitado globalmente! Todos os usuários agora precisarão usar 2FA no login.', {
          duration: 5000
        })
      } else {
        toast.success('✅ 2FA desabilitado globalmente! Os usuários agora podem fazer login sem código 2FA.', {
          duration: 5000
        })
      }
    },
    onError: (error: any) => {
      // Tratar erro 403 (Forbidden) - apenas ADMINs podem configurar
      if (error.response?.status === 403) {
        toast.error('Acesso Negado: Apenas administradores podem configurar 2FA global.')
        return
      }
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        toast.error(getErrorMessage(error))
      }
    },
  })
}

// Hook para status 2FA
export const use2FAStatus = () => {
  return useQuery('2fa-status', authService.get2FAStatus, {
    enabled: authUtils.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  })
}

// Hook para usuário atual
export const useCurrentUser = () => {
  return useQuery(
    'currentUser',
    () => Promise.resolve(authUtils.getCurrentUser()),
    {
      enabled: authUtils.isAuthenticated(),
      staleTime: 5 * 60 * 1000, // 5 minutos
    }
  )
}

// Hook para listar usuários (ADMIN)
export const useUsuarios = () => {
  return useQuery('usuarios', usuarioService.getUsuarios, {
    staleTime: 2 * 60 * 1000, // 2 minutos
  })
}

// Hook para buscar usuário por ID
export const useUsuario = (id: string) => {
  return useQuery(['usuario', id], () => usuarioService.getUsuarioById(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para criar usuário
export const useCreateUsuario = () => {
  const queryClient = useQueryClient()
  
  return useMutation(usuarioService.createUsuario, {
    onSuccess: () => {
      queryClient.invalidateQueries('usuarios')
      queryClient.invalidateQueries('usuarios-ativos')
      toast.success('Usuário criado com sucesso!')
    },
    onError: (error: any) => {
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
        const is2FAError = errorMessage.toLowerCase().includes('código de verificação') || 
                          errorMessage.toLowerCase().includes('código 2fa') ||
                          errorMessage.toLowerCase().includes('verificação') ||
                          errorMessage.toLowerCase().includes('2fa')
        
        toast.error(errorMessage, {
          duration: is2FAError ? 10000 : 8000, // 10 segundos para erros 2FA, 8 para outros
        })
      }
    },
  })
}

// Hook para atualizar usuário
export const useUpdateUsuario = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ id, data }: { id: string; data: UpdateUsuarioRequest }) =>
      usuarioService.updateUsuario(id, data),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries('usuarios')
        queryClient.invalidateQueries(['usuario', id])
        queryClient.invalidateQueries('currentUser')
        toast.success('Usuário atualizado com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      },
    }
  )
}

// Hook para atualizar senha
export const useUpdatePassword = () => {
  return useMutation(
    ({ id, data }: { id: string; data: UpdatePasswordRequest }) =>
      usuarioService.updatePassword(id, data),
    {
      onSuccess: () => {
        toast.success('Senha atualizada com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      },
    }
  )
}

// Hook para upload de foto de perfil
export const useUploadFotoPerfil = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ id, file }: { id: string; file: File }) =>
      usuarioService.uploadFotoPerfil(id, file),
    {
      onSuccess: async (_data, { id }) => {
        // Buscar dados atualizados do usuário do backend
        try {
          const usuarioAtualizado = await usuarioService.getUsuarioById(id)
          
          // Atualizar localStorage com dados atualizados
          localStorage.setItem('user', JSON.stringify(usuarioAtualizado))
          
          // Disparar evento para atualizar o contexto
          window.dispatchEvent(new CustomEvent('auth-login', { 
            detail: usuarioAtualizado 
          }))
        } catch (error) {
          console.error('Erro ao buscar usuário atualizado:', error)
        }
        
        queryClient.invalidateQueries('usuarios')
        queryClient.invalidateQueries(['usuario', id])
        queryClient.invalidateQueries('currentUser')
        queryClient.setQueryData('currentUser', async () => {
          try {
            return await usuarioService.getUsuarioById(id)
          } catch {
            return null
          }
        })
        toast.success('Foto de perfil atualizada com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          const errorMessage = error.response?.data?.erro || getErrorMessage(error)
          toast.error(errorMessage)
        }
      },
    }
  )
}

// Hook para remover foto de perfil
export const useRemoverFotoPerfil = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    (id: string) => usuarioService.removerFotoPerfil(id),
    {
      onSuccess: async (_, id) => {
        // Buscar dados atualizados do usuário do backend
        try {
          const usuarioAtualizado = await usuarioService.getUsuarioById(id)
          
          // Atualizar localStorage com dados atualizados
          localStorage.setItem('user', JSON.stringify(usuarioAtualizado))
          
          // Disparar evento para atualizar o contexto
          window.dispatchEvent(new CustomEvent('auth-login', { 
            detail: usuarioAtualizado 
          }))
        } catch (error) {
          console.error('Erro ao buscar usuário atualizado:', error)
        }
        
        queryClient.invalidateQueries('usuarios')
        queryClient.invalidateQueries(['usuario', id])
        queryClient.invalidateQueries('currentUser')
        queryClient.setQueryData('currentUser', async () => {
          try {
            return await usuarioService.getUsuarioById(id)
          } catch {
            return null
          }
        })
        toast.success('Foto de perfil removida com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          const errorMessage = error.response?.data?.erro || getErrorMessage(error)
          toast.error(errorMessage)
        }
      },
    }
  )
}

// Hook para desativar usuário (ADMIN)
export const useDeactivateUsuario = () => {
  const queryClient = useQueryClient()
  
  return useMutation(usuarioService.deactivateUsuario, {
    onSuccess: () => {
      queryClient.invalidateQueries('usuarios')
      toast.success('Usuário desativado com sucesso!')
    },
    onError: (error: any) => {
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
        const is2FAError = errorMessage.toLowerCase().includes('código de verificação') || 
                          errorMessage.toLowerCase().includes('código 2fa') ||
                          errorMessage.toLowerCase().includes('verificação') ||
                          errorMessage.toLowerCase().includes('2fa')
        
        toast.error(errorMessage, {
          duration: is2FAError ? 10000 : 8000, // 10 segundos para erros 2FA, 8 para outros
        })
      }
    },
  })
}

// Hook para alterar status do usuário
export const useAlterarStatusUsuario = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ id, ativo }: { id: string; ativo: boolean }) =>
      usuarioService.alterarStatusUsuario(id, ativo),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('usuarios')
        queryClient.invalidateQueries('usuarios-ativos')
        toast.success('Status do usuário alterado com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      },
    }
  )
}

// Hook para remover usuário
export const useDeleteUsuario = () => {
  const queryClient = useQueryClient()
  
  return useMutation(usuarioService.deleteUsuario, {
    onSuccess: () => {
      queryClient.invalidateQueries('usuarios')
      queryClient.invalidateQueries('usuarios-ativos')
      toast.success('Usuário removido com sucesso!')
    },
    onError: (error: any) => {
      // Só mostrar toast se o interceptor não mostrou ainda
      if (!isErrorHandled(error)) {
        const errorMessage = getErrorMessage(error)
        // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
        const is2FAError = errorMessage.toLowerCase().includes('código de verificação') || 
                          errorMessage.toLowerCase().includes('código 2fa') ||
                          errorMessage.toLowerCase().includes('verificação') ||
                          errorMessage.toLowerCase().includes('2fa')
        
        toast.error(errorMessage, {
          duration: is2FAError ? 10000 : 8000, // 10 segundos para erros 2FA, 8 para outros
        })
      }
    },
  })
}

// Hook para buscar usuários ativos
export const useUsuariosAtivos = () => {
  return useQuery('usuarios-ativos', usuarioService.getUsuariosAtivos, {
    staleTime: 2 * 60 * 1000, // 2 minutos
  })
}

// Hook para buscar usuário por email
export const useUsuarioByEmail = (email: string) => {
  return useQuery(
    ['usuario-email', email],
    () => usuarioService.getUsuarioByEmail(email),
    {
      enabled: !!email,
      staleTime: 5 * 60 * 1000,
    }
  )
}

// Hook para buscar usuários por nome
export const useUsuariosByNome = (nome: string) => {
  return useQuery(
    ['usuarios-nome', nome],
    () => usuarioService.getUsuariosByNome(nome),
    {
      enabled: !!nome && nome.length > 2,
      staleTime: 2 * 60 * 1000,
    }
  )
}

// Hook para contar usuários ativos
export const useContarUsuariosAtivos = () => {
  return useQuery('contar-ativos', usuarioService.contarUsuariosAtivos, {
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para contar usuários inativos
export const useContarUsuariosInativos = () => {
  return useQuery('contar-inativos', usuarioService.contarUsuariosInativos, {
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para listar usuários por status
export const useUsuariosByStatus = (status: 'ativo' | 'inativo') => {
  return useQuery(['usuarios-status', status], () => usuarioService.getUsuariosByStatus(status), {
    enabled: !!status,
    staleTime: 5 * 60 * 1000,
  })
}

// ===== HOOKS PARA CAMPANHAS =====

// Hook para listar todas as campanhas
export const useCampanhas = () => {
  return useQuery('campanhas', campanhaService.getCampanhas, {
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para buscar campanha por ID
export const useCampanhaById = (id: string) => {
  return useQuery(['campanha', id], () => campanhaService.getCampanhaById(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para buscar campanhas por ano
export const useCampanhasByAno = (ano: string) => {
  return useQuery(['campanhas-ano', ano], () => campanhaService.getCampanhasByAno(ano), {
    enabled: !!ano,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para buscar participantes da campanha
export const useParticipantesCampanha = (id: string) => {
  return useQuery(['participantes-campanha', id], () => campanhaService.getParticipantesCampanha(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para contar participantes da campanha
export const useContarParticipantesCampanha = (id: string) => {
  return useQuery(['contar-participantes', id], () => campanhaService.contarParticipantesCampanha(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para buscar participantes por sexo
export const useParticipantesBySexo = (id: string, sexo: string) => {
  return useQuery(['participantes-sexo', id, sexo], () => campanhaService.getParticipantesBySexo(id, sexo), {
    enabled: !!id && !!sexo,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para buscar participantes por UF
export const useParticipantesByUf = (id: string, uf: string) => {
  return useQuery(['participantes-uf', id, uf], () => campanhaService.getParticipantesByUf(id, uf), {
    enabled: !!id && !!uf,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook para buscar campanhas com filtros
export const useCampanhasWithFilters = (filters: CampanhaFilters) => {
  return useQuery(['campanhas-filtros', filters], () => campanhaService.getCampanhasWithFilters(filters), {
    enabled: Object.keys(filters).length > 0,
    staleTime: 5 * 60 * 1000,
  })
}

  // Hook para obter estatísticas das campanhas
  export const useCampanhaStats = () => {
    return useQuery('campanhas-stats', campanhaService.getCampanhaStats, {
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar campanhas por tipo
  export const useCampanhasByTipo = (tipo: string) => {
    return useQuery(['campanhas-tipo', tipo], () => campanhaService.getCampanhasByTipo(tipo), {
      enabled: !!tipo,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar campanhas por abrangência
  export const useCampanhasByAbrangencia = (abrangencia: string) => {
    return useQuery(['campanhas-abrangencia', abrangencia], () => campanhaService.getCampanhasByAbrangencia(abrangencia), {
      enabled: !!abrangencia,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar campanhas ativas
  export const useCampanhasAtivas = () => {
    return useQuery('campanhas-ativas', campanhaService.getCampanhasAtivas, {
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar campanhas por participante
  export const useCampanhasByParticipante = (participanteId: string) => {
    return useQuery(['campanhas-participante', participanteId], () => campanhaService.getCampanhasByParticipante(participanteId), {
      enabled: !!participanteId,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para listar campanhas inativas (apenas ADMIN)
  export const useCampanhasInativas = (enabled: boolean = true) => {
    return useQuery('campanhas-inativas', campanhaService.getCampanhasInativas, {
      enabled: enabled, // Só busca se enabled for true
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hooks de Monitoramento e Auditoria (apenas ADMIN)
  export const useSessoesAtivas = (enabled: boolean = true) => {
    return useQuery('sessoes-ativas', monitoramentoService.getSessoesAtivas, {
      enabled: enabled,
      staleTime: 30 * 1000, // 30 segundos
      refetchInterval: 60 * 1000, // Atualizar a cada 1 minuto
    })
  }

  export const useSessoesPorRole = (role: 'ADMIN' | 'USER', enabled: boolean = true) => {
    return useQuery(
      ['sessoes-role', role],
      () => monitoramentoService.getSessoesPorRole(role),
      {
        enabled: enabled,
        staleTime: 30 * 1000,
      }
    )
  }

  export const useEstatisticasSessoes = (enabled: boolean = true) => {
    return useQuery('estatisticas-sessoes', monitoramentoService.getEstatisticasSessoes, {
      enabled: enabled,
      staleTime: 30 * 1000,
      refetchInterval: 60 * 1000,
    })
  }

  export const useEventos = (params?: {
    page?: number
    size?: number
    sortBy?: string
    sortDir?: 'ASC' | 'DESC'
  }, enabled: boolean = true) => {
    return useQuery(
      ['eventos', params],
      () => monitoramentoService.getEventos(params),
      {
        enabled: enabled,
        staleTime: 10 * 1000, // 10 segundos
      }
    )
  }

  export const useEventosPorUsuario = (usuarioId: string, params?: {
    page?: number
    size?: number
  }, enabled: boolean = true) => {
    return useQuery(
      ['eventos-usuario', usuarioId, params],
      () => monitoramentoService.getEventosPorUsuario(usuarioId, params),
      {
        enabled: enabled && !!usuarioId,
        staleTime: 10 * 1000,
      }
    )
  }

  export const useEventosPorTipo = (tipoEvento: string, params?: {
    page?: number
    size?: number
  }, enabled: boolean = true) => {
    return useQuery(
      ['eventos-tipo', tipoEvento, params],
      () => monitoramentoService.getEventosPorTipo(tipoEvento, params),
      {
        enabled: enabled && !!tipoEvento,
        staleTime: 10 * 1000,
      }
    )
  }

  export const useEventosPorEndpoint = (endpoint: string, params?: {
    page?: number
    size?: number
  }, enabled: boolean = true) => {
    return useQuery(
      ['eventos-endpoint', endpoint, params],
      () => monitoramentoService.getEventosPorEndpoint(endpoint, params),
      {
        enabled: enabled && !!endpoint,
        staleTime: 10 * 1000,
      }
    )
  }

  export const useEventosPorPeriodo = (dataInicio: string, dataFim: string, params?: {
    page?: number
    size?: number
  }, enabled: boolean = true) => {
    return useQuery(
      ['eventos-periodo', dataInicio, dataFim, params],
      () => monitoramentoService.getEventosPorPeriodo(dataInicio, dataFim, params),
      {
        enabled: enabled && !!dataInicio && !!dataFim,
        staleTime: 10 * 1000,
      }
    )
  }

  export const useEventosPorUsuarioEPeriodo = (usuarioId: string, dataInicio: string, dataFim: string, params?: {
    page?: number
    size?: number
  }, enabled: boolean = true) => {
    return useQuery(
      ['eventos-usuario-periodo', usuarioId, dataInicio, dataFim, params],
      () => monitoramentoService.getEventosPorUsuarioEPeriodo(usuarioId, dataInicio, dataFim, params),
      {
        enabled: enabled && !!usuarioId && !!dataInicio && !!dataFim,
        staleTime: 10 * 1000,
      }
    )
  }

  // Hook para listar usuários pendentes de aprovação (apenas ADMIN)
  export const useUsuariosPendentes = (enabled: boolean = true) => {
    return useQuery('usuarios-pendentes', usuarioService.getUsuariosPendentes, {
      enabled: enabled,
      staleTime: 30 * 1000,
      refetchInterval: 60 * 1000, // Atualizar a cada 1 minuto
    })
  }

  // Hook para ativar usuário (aprovar cadastro)
  export const useAtivarUsuario = () => {
    const queryClient = useQueryClient()
    
    return useMutation(usuarioService.ativarUsuario, {
      onSuccess: () => {
        toast.success('Usuário ativado com sucesso!')
        // Invalidar queries relacionadas
        queryClient.invalidateQueries('usuarios')
        queryClient.invalidateQueries('usuarios-pendentes')
      },
      onError: (error: any) => {
        if (!isErrorHandled(error)) {
          const errorMessage = getErrorMessage(error)
          toast.error(errorMessage)
        }
      },
    })
  }

  // Hook para criar campanha (apenas ADMIN)
  export const useCreateCampanha = () => {
    const queryClient = useQueryClient()
    
    return useMutation(campanhaService.createCampanha, {
      onSuccess: () => {
        queryClient.invalidateQueries('campanhas')
        queryClient.invalidateQueries('campanhas-ativas')
        queryClient.invalidateQueries('campanhas-inativas')
        queryClient.invalidateQueries('campanhas-stats')
        toast.success('Campanha criada com sucesso!')
      },
      onError: (error: any) => {
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      },
    })
  }

  // Hook para editar campanha (apenas ADMIN)
  export const useUpdateCampanha = () => {
    const queryClient = useQueryClient()
    
    return useMutation(
      ({ id, data }: { id: string; data: UpdateCampanhaRequest }) => campanhaService.updateCampanha(id, data),
      {
        onSuccess: (_, { id }) => {
          queryClient.invalidateQueries('campanhas')
          queryClient.invalidateQueries(['campanha', id])
          queryClient.invalidateQueries('campanhas-ativas')
          queryClient.invalidateQueries('campanhas-inativas')
          queryClient.invalidateQueries('campanhas-stats')
          toast.success('Campanha atualizada com sucesso!')
        },
        onError: (error: any) => {
          if (!isErrorHandled(error)) {
            toast.error(getErrorMessage(error))
          }
        },
      }
    )
  }

  // Hook para ativar campanha (apenas ADMIN)
  export const useAtivarCampanha = () => {
    const queryClient = useQueryClient()
    
    return useMutation(
      (id: string) => campanhaService.ativarCampanha(id),
      {
        onSuccess: (_, id) => {
          queryClient.invalidateQueries('campanhas')
          queryClient.invalidateQueries(['campanha', id])
          queryClient.invalidateQueries('campanhas-ativas')
          queryClient.invalidateQueries('campanhas-inativas')
          queryClient.invalidateQueries('campanhas-stats')
          toast.success('Campanha ativada com sucesso!')
        },
        onError: (error: any) => {
          if (!isErrorHandled(error)) {
            toast.error(getErrorMessage(error))
          }
        },
      }
    )
  }

  // Hook para desativar campanha (apenas ADMIN)
  export const useDesativarCampanha = () => {
    const queryClient = useQueryClient()
    
    return useMutation(
      (id: string) => campanhaService.desativarCampanha(id),
      {
        onSuccess: (_, id) => {
          queryClient.invalidateQueries('campanhas')
          queryClient.invalidateQueries(['campanha', id])
          queryClient.invalidateQueries('campanhas-ativas')
          queryClient.invalidateQueries('campanhas-inativas')
          queryClient.invalidateQueries('campanhas-stats')
          toast.success('Campanha desativada com sucesso!')
        },
        onError: (error: any) => {
          if (!isErrorHandled(error)) {
            toast.error(getErrorMessage(error))
          }
        },
      }
    )
  }

  // ===== HOOKS PARA PARTICIPANTES =====

  // Hook para listar participantes
  export const useParticipantes = (page: number = 0, size: number = 10) => {
    return useQuery(['participantes', page, size], () => participanteService.getParticipantes(page, size), {
      staleTime: 2 * 60 * 1000,
    })
  }

  // Hook para buscar participante por ID
  export const useParticipanteById = (id: string) => {
    return useQuery(['participante', id], () => participanteService.getParticipanteById(id), {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar participantes por nome
  export const useParticipantesByNome = (nome: string) => {
    return useQuery(['participantes-nome', nome], () => participanteService.getParticipantesByNome(nome), {
      enabled: !!nome,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar participantes por CPF
  export const useParticipantesByCpf = (cpf: string) => {
    return useQuery(['participantes-cpf', cpf], () => participanteService.getParticipantesByCpf(cpf), {
      enabled: !!cpf,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar participantes por campanha (paginado)
  export const useParticipantesByCampanha = (
    idCampanha: string, 
    page: number = 0, 
    size: number = 10, 
    sortBy: string = 'nome', 
    sortDir: string = 'asc'
  ) => {
    return useQuery(['participantes-campanha', idCampanha, page, size, sortBy, sortDir], () => {
      return participanteService.getParticipantesByCampanha(idCampanha, page, size, sortBy, sortDir)
    }, {
      enabled: !!idCampanha,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar participantes por sexo
  export const useParticipantesBySexoFilter = (sexo: string) => {
    return useQuery(['participantes-sexo', sexo], () => participanteService.getParticipantesBySexo(sexo), {
      enabled: !!sexo,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para buscar participantes por status
  export const useParticipantesByStatus = (status: string) => {
    return useQuery(['participantes-status', status], () => participanteService.getParticipantesByStatus(status), {
      enabled: !!status,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para busca avançada
  export const useParticipantesAvancada = (filters: ParticipanteFilters) => {
    return useQuery(['participantes-avancada', filters], () => participanteService.getParticipantesAvancada(filters), {
      enabled: Object.keys(filters).length > 0,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para contar total de participantes
  export const useContarTotalParticipantes = () => {
    return useQuery('contar-total-participantes', participanteService.contarTotalParticipantes, {
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para contar participantes do dia atual
  export const useContarParticipantesHoje = () => {
    return useQuery('contar-participantes-hoje', participanteService.contarParticipantesHoje, {
      staleTime: 1 * 60 * 1000, // Cache por 1 minuto (dados mais dinâmicos)
    })
  }

  // Hook para contar participantes por campanha
  export const useContarParticipantesByCampanha = (idCampanha: string) => {
    return useQuery(['contar-participantes-campanha', idCampanha], () => {
      return participanteService.contarParticipantesByCampanha(idCampanha)
    }, {
      enabled: !!idCampanha,
      staleTime: 5 * 60 * 1000,
    })
  }

  // Hook para obter estatísticas dos participantes
  export const useParticipanteStats = () => {
    return useQuery('participantes-stats', participanteService.getParticipanteStats, {
      staleTime: 5 * 60 * 1000,
    })
  }

// Hooks para Parâmetros de Sorteio
export const useParametrosSorteio = (enabled: boolean = true) => {
  return useQuery('parametros-sorteio', parametroSorteioService.getParametros, {
    staleTime: 5 * 60 * 1000,
    enabled: enabled
  })
}

export const useParametrosSorteioAtivos = (enabled: boolean = true) => {
  return useQuery('parametros-sorteio-ativos', parametroSorteioService.getParametrosAtivos, {
    staleTime: 5 * 60 * 1000,
    enabled: enabled
  })
}

export const useParametrosSorteioInativos = (enabled: boolean = true) => {
  return useQuery('parametros-sorteio-inativos', parametroSorteioService.getParametrosInativos, {
    staleTime: 5 * 60 * 1000,
    enabled: enabled
  })
}

export const useParametroSorteioById = (id: string) => {
  return useQuery(['parametro-sorteio', id], () => parametroSorteioService.getParametroById(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

export const useParametrosSorteioByCampanha = (campanhaId: string) => {
  return useQuery(['parametros-sorteio-campanha', campanhaId], () => parametroSorteioService.getParametrosByCampanha(campanhaId), {
    enabled: !!campanhaId,
    staleTime: 5 * 60 * 1000,
  })
}

export const useParametrosSorteioByAno = (ano: number) => {
  return useQuery(['parametros-sorteio-ano', ano], () => parametroSorteioService.getParametrosByAno(ano), {
    enabled: !!ano,
    staleTime: 5 * 60 * 1000,
  })
}

export const useParametrosSorteioByCriterio = (criterio: string) => {
  return useQuery(['parametros-sorteio-criterio', criterio], () => parametroSorteioService.getParametrosByCriterio(criterio), {
    enabled: !!criterio,
    staleTime: 5 * 60 * 1000,
  })
}

export const useParametrosSorteioMultiplasInscricoes = () => {
  return useQuery('parametros-sorteio-multiplas', parametroSorteioService.getParametrosMultiplasInscricoes, {
    staleTime: 5 * 60 * 1000,
  })
}

export const useParametrosSorteioValidos = () => {
  return useQuery('parametros-sorteio-validos', parametroSorteioService.getParametrosValidos, {
    staleTime: 5 * 60 * 1000,
  })
}

export const useContarParametrosSorteioAtivos = (enabled: boolean = true) => {
  return useQuery('contar-parametros-sorteio-ativos', parametroSorteioService.contarParametrosAtivos, {
    staleTime: 5 * 60 * 1000,
    enabled: enabled
  })
}

export const useContarParametrosSorteioInativos = (enabled: boolean = true) => {
  return useQuery('contar-parametros-sorteio-inativos', parametroSorteioService.contarParametrosInativos, {
    staleTime: 5 * 60 * 1000,
    enabled: enabled
  })
}

export const useContarParametrosSorteioByCampanha = (campanhaId: string) => {
  return useQuery(['contar-parametros-sorteio-campanha', campanhaId], () => parametroSorteioService.contarParametrosByCampanha(campanhaId), {
    enabled: !!campanhaId,
    staleTime: 5 * 60 * 1000,
  })
}

export const useParametrosSorteioPaginados = (
  page: number = 0, 
  size: number = 10, 
  sort: string = 'dataCriacao,desc'
) => {
  return useQuery(['parametros-sorteio-paginados', page, size, sort], () => 
    parametroSorteioService.getParametrosPaginados(page, size, sort), {
    staleTime: 5 * 60 * 1000,
  })
}

export const useCreateParametroSorteio = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ data, criadoPor }: { data: CreateParametroSorteioRequest, criadoPor: string }) => 
      parametroSorteioService.createParametro(data, criadoPor),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('parametros-sorteio')
        toast.success('Parâmetro de sorteio criado com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

export const useUpdateParametroSorteio = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ id, data }: { id: string, data: any }) => 
      parametroSorteioService.updateParametro(id, data as UpdateParametroSorteioRequest),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('parametros-sorteio')
        toast.success('Parâmetro de sorteio atualizado com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

export const useAlterarStatusParametroSorteio = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ id, ativo }: { id: string, ativo: boolean }) => 
      parametroSorteioService.alterarStatusParametro(id, ativo),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('parametros-sorteio')
        toast.success('Status do parâmetro alterado com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

export const useDeleteParametroSorteio = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    (id: string) => parametroSorteioService.deleteParametro(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('parametros-sorteio')
        toast.success('Parâmetro de sorteio removido com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

// Hooks para Configurações
export const useConfiguracoes = () => {
  return useQuery('configuracoes', configuracoesService.getConfiguracoes)
}

export const useConfiguracoesAtivas = () => {
  // Verificar se é ADMIN através do token/user armazenado
  const user = authUtils.getCurrentUser()
  const isAdmin = user?.role === 'ADMIN'
  
  return useQuery(
    'configuracoes-ativas', 
    configuracoesService.getConfiguracoesAtivas,
    {
      enabled: isAdmin, // Só buscar se for ADMIN
      retry: false, // Não tentar novamente se der erro 403
      onError: (error: any) => {
        if (error.response?.status === 403) {
          // Erro 403 já tratado no interceptor, apenas marcar como tratado
          error._errorHandled = true
        }
      }
    }
  )
}

export const useConfiguracoesPorCategoria = (categoria: string) => {
  return useQuery(
    ['configuracoes-categoria', categoria], 
    () => configuracoesService.getConfiguracoesPorCategoria(categoria),
    { enabled: !!categoria }
  )
}

export const useTempoSessao = () => {
  // Verificar se é ADMIN através do token/user armazenado
  const user = authUtils.getCurrentUser()
  const isAdmin = user?.role === 'ADMIN'
  
  return useQuery(
    'tempo-sessao', 
    configuracoesService.getTempoSessao,
    {
      enabled: isAdmin, // Só buscar se for ADMIN
      retry: false, // Não tentar novamente se der erro 403
      onError: (error: any) => {
        if (error.response?.status === 403) {
          // Erro 403 já tratado no interceptor, apenas marcar como tratado
          error._errorHandled = true
        }
      }
    }
  )
}

export const useUpdateTempoSessao = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    (minutos: number) => configuracoesService.updateTempoSessao(minutos),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('tempo-sessao')
        queryClient.invalidateQueries('configuracoes')
        const mensagem = data?.mensagem || data?.global 
          ? 'Tempo de sessão GLOBAL atualizado com sucesso!'
          : 'Tempo de sessão atualizado com sucesso!'
        toast.success(mensagem)
      },
      onError: (error: any) => {
        // Tratar erro 403 (Forbidden)
        if (error.response?.status === 403) {
          toast.error('Acesso negado. Apenas administradores podem alterar configurações do sistema.')
          return
        }
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

export const useUpdateTempoRefresh = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    (minutos: number) => configuracoesService.updateTempoRefresh(minutos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tempo-sessao')
        queryClient.invalidateQueries('configuracoes')
        toast.success('Tempo de refresh atualizado com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

export const useCreateConfiguracao = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    (data: Omit<ConfiguracaoSistema, 'id' | 'dataCriacao' | 'dataAtualizacao'>) => 
      configuracoesService.createConfiguracao(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('configuracoes')
        queryClient.invalidateQueries('configuracoes-ativas')
        toast.success('Configuração criada com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

export const useInicializarConfiguracoes = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    () => configuracoesService.inicializarConfiguracoes(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('configuracoes')
        queryClient.invalidateQueries('configuracoes-ativas')
        queryClient.invalidateQueries('tempo-sessao')
        toast.success('Configurações inicializadas com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

// ===== HOOKS PARA SORTEIOS INDIVIDUAIS =====

// Listar sorteios disponíveis
export const useSorteiosDisponiveis = (parametroSorteioId: string | null, enabled: boolean = true) => {
  return useQuery(
    ['sorteios-disponiveis', parametroSorteioId],
    () => sorteioService.getSorteiosDisponiveis(parametroSorteioId!),
    {
      enabled: enabled && !!parametroSorteioId,
      staleTime: 30 * 1000, // 30 segundos
    }
  )
}

// Status dos sorteios
export const useStatusSorteios = (parametroSorteioId: string | null, enabled: boolean = true) => {
  return useQuery(
    ['status-sorteios', parametroSorteioId],
    () => sorteioService.getStatusSorteios(parametroSorteioId!),
    {
      enabled: enabled && !!parametroSorteioId,
      staleTime: 30 * 1000, // 30 segundos
    }
  )
}

// Validar sorteio específico
export const useValidarSorteio = (parametroSorteioId: string | null, numeroSorteio: number | null, enabled: boolean = true) => {
  return useQuery(
    ['validar-sorteio', parametroSorteioId, numeroSorteio],
    () => sorteioService.validarSorteio(parametroSorteioId!, numeroSorteio!),
    {
      enabled: enabled && !!parametroSorteioId && !!numeroSorteio,
      staleTime: 30 * 1000, // 30 segundos
    }
  )
}

// Executar sorteio individual
export const useExecutarSorteioIndividual = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ parametroSorteioId, numeroSorteio, executadoPor }: { 
      parametroSorteioId: string
      numeroSorteio: number
      executadoPor: string 
    }) => sorteioService.executarSorteioIndividual(parametroSorteioId, numeroSorteio, executadoPor),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sorteios-disponiveis')
        queryClient.invalidateQueries('status-sorteios')
        queryClient.invalidateQueries('validar-sorteio')
        toast.success('Sorteio executado com sucesso!')
      },
      onError: (error: any) => {
        // Só mostrar toast se o interceptor não mostrou ainda
        if (!isErrorHandled(error)) {
          toast.error(getErrorMessage(error))
        }
      }
    }
  )
}

// ===== HOOKS PARA RESULTADOS DOS SORTEIOS =====

// Listar campanhas com sorteios
export const useCampanhasComSorteios = (enabled: boolean = true) => {
  return useQuery(
    'campanhas-com-sorteios',
    () => sorteioService.getCampanhasComSorteios(),
    {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
    }
  )
}

// Todos os sorteados de uma campanha
export const useResultadosCampanha = (campanhaId: string | null, enabled: boolean = true) => {
  return useQuery(
    ['resultados-campanha', campanhaId],
    () => sorteioService.getResultadosCampanha(campanhaId!),
    {
      enabled: enabled && !!campanhaId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  )
}

// Sorteados de um sorteio específico
export const useResultadosSorteio = (execucaoId: string | null, enabled: boolean = true) => {
  return useQuery(
    ['resultados-sorteio', execucaoId],
    () => sorteioService.getResultadosSorteio(execucaoId!),
    {
      enabled: enabled && !!execucaoId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  )
}

// Lista de sorteios executados por campanha
export const useSorteiosExecutadosCampanha = (campanhaId: string | null, enabled: boolean = true) => {
  return useQuery(
    ['sorteios-executados-campanha', campanhaId],
    () => sorteioService.getSorteiosExecutadosCampanha(campanhaId!),
    {
      enabled: enabled && !!campanhaId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  )
}
